import {Injectable} from '@angular/core';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {PlanService} from '@docma-mm-core/plan/plan.service';
import {Plan} from '@docma-mm-core/plan/plan';
import {Structure} from '@docma-mm-core/structure/structure';
import {BehaviorSubject, firstValueFrom, of, Subject} from 'rxjs';
import {filter, first, map, switchMap, tap} from 'rxjs/operators';
import {MmFilterModel} from '../../../mm-filter/mm-filter.model';
import {MmFilterService} from '../../../mm-filter/mm-filter.service';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {MmFaultsViewService} from '../../mm-faults-view/services/mm-faults-view.service';
import {MmFaultDetailFormComponent} from '../../mm-fault/mm-fault-detail-form/mm-fault-detail-form.component';
import {MmPlanEditService} from '../mm-plan-view/services/mm-plan-edit.service';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {TranslateService} from '@ngx-translate/core';
import {PlanEditModeEnum} from '../mm-plan-view/services/plan-edit-mode.enum';
import {convertBpToMmPlan, getChildPlansOfStructure, getParentPlansOfStructure, sortPlans} from './mm-plan-utils';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {MmFaultFieldMemoryService} from '../../mm-fault/mm-fault-detail-form/services/mm-fault-field-memory.service';
import {FieldConfig as FaultFieldConfig} from '@docma-mm-core/field-config/field-config';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmPlansFilterService} from './mm-plans-filter.service';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {Project} from '@docma-mm-core/project/project';
import {TaskService, TaskStatusEnum} from '@mydocma/task';
import {PlanDto} from '../../../bp-common/models/plan';
import {CoEventService} from '@mydocma/co';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {PersonalizationService} from '@mydocma/app-container';
import {Logger} from '@mydocma/common/src/lib/logger';
import {FieldConfigurationService} from '../../mm-fault/mm-field-configuration/services/field-configuration.service';

@Injectable()
export class MmPlansManagerService {

  faultsViewService: MmFaultsViewService;

  private selectedPlanSubject = new BehaviorSubject<Plan>(null);
  selectedPlan$ = this.selectedPlanSubject.asObservable();

  private showPlanFlyoutSubject = new BehaviorSubject<boolean>(false);
  showPlanFlyout$ = this.showPlanFlyoutSubject.asObservable();

  private planLoadingSubject = new BehaviorSubject<boolean>(false);
  planLoading$ = this.planLoadingSubject.asObservable();

  private plansByStructureSubject = new BehaviorSubject<Plan[]>([]);
  plansByStructure$ = this.plansByStructureSubject.asObservable();

  private allPlansSubject = new BehaviorSubject<Plan[]>([]);
  allPlans$ = this.allPlansSubject.asObservable();

  private currentFilterModel: MmFilterModel = null;
  private treeStructure: Structure;
  private structeredPlans: Plan[];

  private faultDetail: MmFaultDetailFormComponent;
  private cachedStructureId: number;

  private flatSortedStructureList: Structure[];

  public isPlanCorrupt = false;
  public isPlanRendering = false;

  private resetFiltermodel = false;

  public get showPlanFlyout(): boolean {
    return this.showPlanFlyoutSubject.value;
  }

  private currentProject: Project;

  private registeredPlanRenderings = new Map<string, Plan>();
  public onPlanRenderingRegistered = new Subject<Plan>();

  private planStoppedRenderingSubject = new BehaviorSubject<Plan>(null);
  public onPlanStoppedRendering = this.planStoppedRenderingSubject.asObservable();

  constructor(private readonly planService: PlanService,
              private readonly filterService: MmFilterService,
              private readonly loaderService: MmProjectLoaderService,
              private readonly planEditService: MmPlanEditService,
              private readonly dialogService: PromptDialogService,
              private readonly translateService: TranslateService,
              private readonly faultFieldMemoryService: MmFaultFieldMemoryService,
              public deviceService: DeviceService,
              private readonly coEventService: CoEventService,
              private readonly plansFilterService: MmPlansFilterService,
              private readonly projectService: MmProjectService,
              private readonly taskService: TaskService,
              private readonly notificationService: NotificationService,
              private readonly personalizationService: PersonalizationService,
              private readonly logger: Logger,
              private readonly fieldConfigurationService: FieldConfigurationService) {

    this.initSubscrpitions();
    this.planEditService.attachTo(this);
  }

  private initSubscrpitions(): void {

    this.taskService.onTaskChanged.subscribe(async task => {
      console.log('MmPlansManagerService - Task changed:', task);
      // TODO: MMW-2787: handle bp tasks too

      if (this.selectedPlan && this.selectedPlan?.data?.BpPlan?.activeVersion?.task?.id === task.id) {
        if (task.name === 'bp_plan_render' && task.status.value === TaskStatusEnum.ended) {

          const renderedPlan = await firstValueFrom(this.planService.getPlanById(this.selectedPlan.data.BpPlan.id));
          this.updatePlanInView(renderedPlan);
        }
      }
    });

    this.projectService.currentProject.subscribe(p => this.currentProject = p);


    this.coEventService.onPlanDeleted.subscribe(plan => {
      this.removePlanFromView(plan);
    });

    // WIP
    this.coEventService.onPlanCreated.subscribe(plan => {

      const createdPlan = new Plan();
      const structuresByUid: { [Uid: string]: Structure } = Object.fromEntries(this.flatSortedStructureList.map(m => [m.UniqueId, m]));

      const planData = convertBpToMmPlan({...plan}, structuresByUid, this.currentProject);
      createdPlan.id = plan.id;

      createdPlan.loadState = new LoadState({status: LoadStatusEnum.Loaded, date: new Date(Date.now()), data: planData});

      this.allPlansSubject.next(sortPlans(this.flatSortedStructureList, [...this.allPlansSubject.value, createdPlan]));
      this.plansByStructureSubject.next(sortPlans(this.flatSortedStructureList, [...this.plansByStructureSubject.value, createdPlan]));
      this.plansFilterService.registerFilter(this.plansByStructureSubject.value);
    });

    // WIP - This is triggered either by updating OR rendering finish?
    this.coEventService.onPlanUpdated
      .subscribe(plan => {

        if (!plan.isDisabled) {
          const updatedPlan = this.updatePlanInView(plan);
        } else {
          this.removePlanFromView(plan);
        }
      });

    this.loaderService.plans.pipe(
      tap(plans => this.resetFiltermodel = plans.data.length === 0),
      filter(plans => plans.loadState.status === LoadStatusEnum.Loaded),
      switchMap(plans =>
        this.loaderService.structures.pipe(filter(st => st.loadState.status === LoadStatusEnum.Loaded),
          map(structures => ({plans, structures})))
      ),
      map(r => {
          const rootStr = (r.structures.data.length > 0 ? r.structures.data[0] : null);
          return ({
            plans: r.plans.data.map(planData => {
              const plan = new Plan();
              const strId = planData.Structure?.Id;
              if (strId && rootStr) {
                const str = new Structure(rootStr).findById(strId);
                planData.Structure.StructurePathShort = str?.StructurePathShort + ' - ' + str?.Name2;
              }
              plan.loadState = new LoadState({status: LoadStatusEnum.Loaded, date: new Date(Date.now()), data: planData});
              return plan;
            }), root: rootStr
          });
        }
      ),
      tap(param => {
        this.flatSortedStructureList = new Structure(param.root)?.getFlatSortedStructures() || [];
        const filteredPlans = param.plans.filter(plan => !plan.data.BpPlan || plan.data.BpPlan?.isDisabled === false);
        const sortedPlans = sortPlans(this.flatSortedStructureList, filteredPlans);
        this.allPlansSubject.next(sortedPlans);
        this.currentFilterModel = null;
        if (this.selectedPlan) {
          this.selectedPlanSubject.next(sortedPlans.find(p => p.id === this.selectedPlan.id));
        }
        // if (!this.currentFilterModel || this.currentFilterModel?.structure.Id === this.currentFilterModel?.structure.RootId) {
        //   this.plansByStructureSubject.next(sortedPlans);
        // }
      }),
      switchMap(plans =>
        // reaction to stucture change in filter
        this.filterService.state.pipe(filter(filterState => filterState.structure !== undefined),
          filter(filterState => filterState?.structure?.Id !== this.currentFilterModel?.structure?.Id))
      ),
      tap(filterState => {
        this.currentFilterModel = filterState;
      }),
      switchMap(filterState => this.loaderService.structures.pipe(filter(struct => struct.data.length > 0))),
      filter(structures => structures.data[0].RootId === this.currentFilterModel?.structure.RootId || structures.data[0].RootId === this.currentFilterModel?.structure.Id),
      tap(structures => {
        this.treeStructure = structures.data[0];
        this.flatSortedStructureList = new Structure(this.treeStructure).getFlatSortedStructures();
        const allActivePlans = sortPlans(this.flatSortedStructureList, this.allPlans).filter(plan => !plan.data.BpPlan || plan.data.BpPlan?.isDisabled === false);
        this.allPlansSubject.next(allActivePlans);
      }),
      switchMap(structures => {
        if (this.allPlans?.length > 0) {
          if (this.currentFilterModel.structure.Id !== this.currentFilterModel.structure.RootId) {
            const currentStructure = this.flatSortedStructureList.find(x => x.Id === this.currentFilterModel.structure.Id);
            if (currentStructure) {
              this.structeredPlans = this.filterPlansByStructure(currentStructure, this.allPlansSubject.value);
            } else {
              this.structeredPlans = this.allPlansSubject.value;
            }
          } else {
            this.structeredPlans = this.allPlansSubject.value;
          }
        } else {
          this.structeredPlans = [];
        }
        return of(this.structeredPlans);
      }),
    ).subscribe(async plans => {
      await this.changedFilterState();

      if (this.resetFiltermodel) {
        this.currentFilterModel = null;
      }
      this.plansByStructureSubject.next(plans as Plan[]);
    });

    this.taskService.onTaskChanged.subscribe(task => {
      console.log('TASK CHANGED', task);
      const observedPlan = this.registeredPlanRenderings.get(task.id);
      if (!observedPlan) {
        return;
      }

      switch (task?.status?.value) {
        case TaskStatusEnum.ended:
          this.notificationService.success(this.translateService.instant('MM.plan.error.planPreparedSuccessToast', {planTitle: this.getTitle(observedPlan)}));
          this.planStoppedRenderingSubject.next(observedPlan);
          this.registeredPlanRenderings.delete(task.id);
          observedPlan.metaData = null;
          break;
        case TaskStatusEnum.canceled:
        case TaskStatusEnum.failed:
          this.logger.error('Plan rendering failed, taskId: ' + task?.id, '', 'MmPlansManagerService');
          this.notificationService.error(this.translateService.instant('MM.plan.error.planPreparedErrorToast', {planTitle: this.getTitle(observedPlan)}));
          this.planStoppedRenderingSubject.next(observedPlan);
          this.registeredPlanRenderings.delete(task.id);
          break;
      }
    });
  }

  attachTo(targetToAttach: any) {
    if (targetToAttach instanceof MmFaultsViewService) {
      this.faultsViewService = targetToAttach;
    }
  }


  private filterPlansByStructure(selectedStructure: Structure, planList: Plan[]): Plan[] {
    const currentLayerPlans = planList.filter((plan) => plan.data.Structure.Id === selectedStructure.Id);

    if (currentLayerPlans.length > 0) {
      return [...currentLayerPlans];
    }

    const parentPlans = getParentPlansOfStructure(selectedStructure, this.treeStructure, planList);
    const childPlans = getChildPlansOfStructure(selectedStructure, planList);
    return [...parentPlans, ...childPlans];
  }


  private async changedFilterState() {
    const leftToggleGroup = await this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup);

    if (this.selectedPlanSubject?.value && !this.structeredPlans.find(plan => this.selectedPlanSubject.value.id === plan.id)) {
      this.setSelectedPlan(null);
    }

    if (this.selectedPlanSubject.value == null && this.structeredPlans.length >= 1 && !!leftToggleGroup) {
      this.setSelectedPlan(this.structeredPlans[0]);
      this.setPlanFlyoutVisible(false);
    } else if (this.selectedPlanSubject.value == null && this.structeredPlans.length >= 1 && !leftToggleGroup) {
      /* MMW-3471 - SPECIAL CASE FOR VERY 1ST APP STARTUP */
      this.setSelectedPlan(this.structeredPlans[0]);
      this.setPlanFlyoutVisible(false);
    }
  }

  async setSelectedPlan(plan: Plan, forceReload = false) {
    if (!this.faultsViewService) {
      return;
    }
    this.cachedStructureId = null;
    this.isPlanCorrupt = false;
    this.isPlanRendering = false;
    if (plan) {
      if (!forceReload && this.selectedPlanSubject?.value?.id === plan?.id && this.selectedPlanSubject?.value?.metaData) {
        return plan;
      }

      this.isPlanCorrupt = false;

      const currentFault = await firstValueFrom(this.faultsViewService.currentFault.pipe(first()));

      let faultStructureId: number;

      if (!this.deviceService.isMobil()) {
        faultStructureId = this.faultDetail?.faultDetailsService?.faultData?.Structure?.Id || currentFault?.data.Structure?.Id;
      } else {
        const fields = await firstValueFrom(this.fieldConfigurationService.fields$.pipe(filter(Boolean)));
        const fieldConfigsDict = Object.fromEntries(fields.filter(x => x.Visibility === true).map(f => [f.Field.Prop, f]));
        const memoryValues = await this.faultFieldMemoryService.getMemoryValues(fieldConfigsDict);

        if (memoryValues?.hasOwnProperty(FieldPropertyEnum.Structure)) {
          faultStructureId = +memoryValues[FieldPropertyEnum.Structure];
        } else {
          faultStructureId = currentFault?.data?.Structure?.Id;
        }
      }


      if (currentFault && !currentFault.isNew
        && this.planEditService.planEditMode === PlanEditModeEnum.Edit
        && !(await this.checkFaultInPlanStructure(faultStructureId, plan.data.Structure.Id))) {
        this.dialogService.showNotificationDialog({
          title: this.translateService.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateService.instant('MM.plan.error.faultPlanStructureInvalid'),
          content: this.translateService.instant('MM.plan.error.selectAnotherPlan'),
        });
        return;
      } else {
        if (currentFault && plan && this.planEditService.planEditMode === PlanEditModeEnum.Edit) {
          this.faultDetail?.faultDetailsService?.setStructure(plan.data.Structure.Id);
        }
      }

      if (forceReload || this.selectedPlanSubject?.value?.id !== plan?.id || !plan.metaData) {
        this.setPlanLoading(true);
        try {
          const isDownloaded = await this.planService.isPlanDownloaded(plan.id.toString());
          plan.metaData = await this.planService.getPlanMetaData(plan.loadState.data, isDownloaded).toPromise();
          if (!plan.metaData) {
            // there should always be metadata of plan or at least task id
            throw new Error();
          }

          let taskId;
          // rendering logic only applies for classic plans for now
          if (!plan.data.BpPlan) {
            if (plan.metaData.TaskId) {
              taskId = plan.metaData.TaskId;

              // if there is no task id and one of the values is empty / 0 -> plan is broken (no notification should be shown because it does not relate to task update)
            } else if (!plan.metaData.ImageHeight || !plan.metaData.ImageWidth || !plan.metaData.MaxZoomLevel || !plan.metaData.TilesUrl || !plan.metaData.TileSize) {
              throw new Error();
            }
          } else {
            // CO plan
            switch (plan.data.BpPlan?.activeVersion?.task?.status) {
              case TaskStatusEnum.ended:
              case TaskStatusEnum.canceled:
              case TaskStatusEnum.failed:
                break;

              default:
                taskId = plan.data.BpPlan?.activeVersion?.task?.id;
                break;
            }
          }

          if (taskId) {
            // check current task status of plan
            const task = await firstValueFrom(this.taskService.loadTask(taskId));
            switch (task?.status?.value) {
              // plan task is still running -> start observing
              case TaskStatusEnum.init:
              case TaskStatusEnum.started:
                this.registeredPlanRenderings.set(plan.metaData.TaskId, plan);
                this.onPlanRenderingRegistered.next(plan);
                this.setPlanLoading(false);
                return;
              case TaskStatusEnum.failed:
              case TaskStatusEnum.canceled:
                this.logger.error('Plan rendering failed, taskId: ' + task?.id, '', 'MmPlansManagerService');
                this.notificationService.error(this.translateService.instant('MM.plan.error.planPreparedErrorToast', {planTitle: this.getTitle(plan)}));
                throw new Error();
            }
          }

        } catch (e) {
          this.setPlanLoading(false);
          this.isPlanCorrupt = true;
        }
      }
    }

    this.setPlanLoading(false);

    if (plan != this.selectedPlanSubject?.value) {
      console.log(plan);
    }

    this.selectedPlanSubject.next(plan);
    return plan;
  }

  async checkFaultInPlanStructure(faultStructureId: number, planStructureId: number): Promise<boolean> {
    const structures = new Structure((await firstValueFrom(this.loaderService.structures.pipe(first(), filter(struct => struct.data.length > 0)))).data[0]);
    return structures.findById(planStructureId)?.findById(faultStructureId) ? true : false;
  }

  setPlanFlyoutVisible(toggle: boolean): void {
    this.showPlanFlyoutSubject.next(toggle);
  }

  setPlanLoading(toggle: boolean): void {
    this.planLoadingSubject.next(toggle);
  }

  setFaultDetail(detailComponent: MmFaultDetailFormComponent): void {
    this.faultDetail = detailComponent;
  }

  get allPlans(): Plan[] {
    return this.allPlansSubject.value;
  }

  get selectedPlan(): Plan {
    return this.selectedPlanSubject.value;
  }

  get plansByStructure(): Plan[] {
    return this.plansByStructureSubject.value;
  }

  async resetFaultStructure(): Promise<void> {
    const currentFault = await firstValueFrom(this.faultsViewService.currentFault.pipe(first()));

    if (this.cachedStructureId) {
      currentFault.data.Structure.Id = this.cachedStructureId;
      this.faultDetail.faultDetailsService.setStructure(this.cachedStructureId);
    }
  }

  getTitle(plan: Plan) {
    if (!plan?.data) {
      return;
    }

    if (!plan.data?.BpPlan) {
      return (plan.data.Description ? plan.data.Description : plan.data.FileName);
    } else {
      return (plan.data.BpPlan.title ? plan.data.BpPlan.title : plan.data.FileName);
    }
  }

  private removePlanFromView(plan: Plan) {
    this.allPlansSubject.next(this.allPlansSubject.value.filter(p => p.id != plan.id));
    this.plansByStructureSubject.next(this.plansByStructureSubject.value.filter(p => p.id != plan.id));
    this.plansFilterService.registerFilter(this.plansByStructureSubject.value);
  }

  private updatePlanInView(plan: PlanDto): Plan {
    const updatedPlan = new Plan();
    const structuresByUid: { [Uid: string]: Structure } = Object.fromEntries(this.flatSortedStructureList.map(m => [m.UniqueId, m]));

    const planData = convertBpToMmPlan({...plan}, structuresByUid, this.currentProject);
    updatedPlan.loadState = new LoadState({status: LoadStatusEnum.Loaded, date: new Date(Date.now()), data: planData});

    this.allPlansSubject.next(sortPlans(this.flatSortedStructureList, [...this.allPlansSubject.value.filter(p => p.id != updatedPlan.id), updatedPlan]));
    this.plansByStructureSubject.next(sortPlans(this.flatSortedStructureList, [...this.plansByStructureSubject.value.filter(p => p.id != updatedPlan.id), updatedPlan]));
    this.plansFilterService.registerFilter(this.plansByStructureSubject.value);

    if (updatedPlan.id == this.selectedPlan.id) {
      this.setSelectedPlan(updatedPlan, true);
    }

    return updatedPlan;
  }
}


